import type { OrderStatus } from "@fprint/shared";
import { cn } from "../lib/cn.js";

/**
 * Shared human-readable order-status labels + badge, used by BOTH the
 * customer SPA (cloud/web Orders page) and the admin SPA (local/admin
 * queue + detail). Keep the wording customer-safe: these strings are
 * shown to end customers, not just operators.
 */
const HUMAN: Record<OrderStatus, string> = {
  queued: "Queued",
  assigned: "Assigned",
  printing: "Printing",
  completed: "Completed",
  shipped: "Shipped",
  pickup_ready: "Pickup ready",
  delivered: "Delivered",
  cancelled: "Cancelled",
  slice_failed: "Slice failed",
  payment_failed: "Payment failed",
};

export function statusLabel(s: OrderStatus): string {
  return HUMAN[s] ?? s;
}

const TONE: Record<OrderStatus, string> = {
  queued: "bg-muted text-fg",
  assigned: "bg-primary/10 text-primary",
  printing: "bg-primary text-primary-fg",
  completed: "bg-accent/20 text-fg",
  shipped: "bg-accent/40 text-fg",
  pickup_ready: "bg-accent/40 text-fg",
  delivered: "bg-accent text-fg",
  cancelled: "bg-muted text-muted-fg",
  // Keep the danger tint background, but use the darkened -fg shade for the
  // text: the vivid --danger (~2.7:1) fails WCAG AA at text-xs, and this badge
  // is now shown to customers (Orders page), not just operators.
  slice_failed: "bg-destructive/20 text-danger-fg",
  payment_failed: "bg-destructive/20 text-danger-fg",
};

export function statusToneClass(s: OrderStatus): string {
  return TONE[s] ?? "bg-muted text-fg";
}

export function StatusBadge({
  status,
  className,
}: {
  status: OrderStatus;
  className?: string;
}) {
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        statusToneClass(status),
        className,
      )}
    >
      {statusLabel(status)}
    </span>
  );
}
